import { useCallback, useState } from 'react';
import { DragEndEvent, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { POINTER_ACTIVATION_DISTANCE_PX, reorderFilters } from './reorderFilters';

interface UseFilterDndArgs {
  order: string[];
  isDraggable: boolean;
  disabled: boolean;
  onReorder: (newOrder: string[]) => void;
}

interface UseFilterDndResult {
  sensors: ReturnType<typeof useSensors>;
  dragDisabled: boolean;
  activeId: string | null;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

/**
 * Encapsulates the DnD wiring for the filter chip list: sensors, active-id
 * tracking, and the reorder side-effect. Pure-by-output: the only mutation it
 * triggers is the `onReorder` callback supplied by the caller.
 */
export const useFilterDnd = ({ order, isDraggable, disabled, onReorder }: UseFilterDndArgs): UseFilterDndResult => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const dragDisabled = !isDraggable || disabled || order.length <= 1;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      if (!event.over) {
        return;
      }
      const next = reorderFilters(order, String(event.active.id), String(event.over.id));
      if (next !== order) {
        onReorder(next);
      }
    },
    [order, onReorder]
  );

  return { sensors, dragDisabled, activeId, handleDragStart, handleDragEnd, handleDragCancel };
};
